import { createSlice } from '@reduxjs/toolkit';
import { Tag, TagWithUsers, userTags } from 'src/models/tag';

interface TagState {
  isLoading: boolean;
  isErrorMessage: string;
  statusCode: number;
  tags: Tag[];
  activeTag: Tag | {};
  tagPaginated: Tag[];
  tagsWithUsers: TagWithUsers[]; 
  page: number;
  limit: number;
  lastVisible: any;
  changeTag: null;
  totalTags: number;
  usersByTag: Record<number, { users: string[]; page: number; pageSize: number }>;
  provisionalTagId: number
  totalPages: number; 
  totalUsers: number; 
  pageSize: number; 
  usersPaginated: any[]; 
  currentPage: number
  selectedTagId: null,
  isLoadingUsers: boolean;
  newTags: Tag[];
  changeNewTag: null;
  uTags: userTags[] | any[];
  pageUtag: number;
  // Nuevos estados para tag individual
  currentTag: any | null;
  isLoadingCurrentTag: boolean;
}

const initialState: TagState = {
  isLoading: false,
  statusCode: 201,
  isErrorMessage: '',
  tags: [],
  activeTag: {},
  tagPaginated: [],
  tagsWithUsers: [], 
  page: 0,
  limit: 25,
  lastVisible: null,
  changeTag: null,
  totalTags: 0,
  usersByTag: {},
  provisionalTagId: 0 ,
  totalPages: 0, 
  totalUsers: 0, 
  usersPaginated: [],
  pageSize: 25, 
  currentPage: 0,
  selectedTagId: null,
  isLoadingUsers: false,
  newTags: [],
  changeNewTag: null,
  uTags: [],
  pageUtag: 0,
  // Estados iniciales para tag individual
  currentTag: null,
  isLoadingCurrentTag: false,
};

export const tagSlice = createSlice({
  name: 'tag',
  initialState,
  reducers: {
    startLoadingTags: (state) => {
      state.isLoading = true;
    },
    stopLoadingTags: (state) => {
      state.isLoading = false;
    },
    setTags: (state, action) => {
      state.isLoading = false;
      state.tags = [...action.payload];
    },
    setTagsPaginated: (state, action) => {
      state.isLoading = false;
      state.page = action.payload.page;
      state.limit = action.payload.limit;
      state.tagPaginated = action.payload.tags;
      state.lastVisible = action.payload.lastVisible;
      state.totalTags = action.payload.totalTags;
      
      state.tags = action.payload.tags
    },
    setTagsWithUsers: (state, action) => {
      state.isLoading = false;
      state.tagsWithUsers = action.payload.results;
      state.totalTags = action.payload.totalTags;
      state.page = action.payload.currentPage;
      state.totalPages = action.payload.totalPages;
    },
    setUsersPaginated: (state, action) => {
      state.isLoading = false;
      state.totalUsers = action.payload.totalUsers;
      state.totalPages = action.payload.totalPages;
      state.currentPage = action.payload.currentPage
      state.usersPaginated = action.payload.data;
    },
    setSelectedTagId: (state, action) => {
      state.selectedTagId = action.payload;
    },
    setChangeTag: (state, action) => {
      const tagId = action.payload;
      state.tags = state.tags.filter(tag => tag.id !== tagId);
      state.tagsWithUsers = state.tagsWithUsers.filter(tagWithUsers => tagWithUsers.tag?.id !== tagId);
    
      state.changeTag = tagId;
    },
    
    setError: (state, action) => {
      const message = action.payload.message;
      state.statusCode = action.payload.status;
      state.isErrorMessage = message;
    },
    setUsersByTag: (state, action) => {
      state.isLoadingUsers = false;
      const { provisionalTagId, users, page, pageSize } = action.payload;
      state.usersByTag[provisionalTagId] = {
        users,
        page,
        pageSize
      };
    },
    setProvisionalTagId: (state, action) => {
      state.provisionalTagId = action.payload;
    },
    setClearUsersTags:(state) => {
      state.usersByTag = {};
      state.provisionalTagId = 0;
      state.totalUsers = 0;
      state.totalPages = 0;
      state.currentPage = 0;
      state.usersPaginated = [];
      state.selectedTagId = null;
      state.uTags = [];
      state.pageUtag = 0;
    },
    startLoadingUsers: (state) => {
      state.isLoadingUsers = true;
    },
    stopLoadingUsers: (state) => {
      state.isLoadingUsers = false;
    },
    setNewTags: (state, action) => {
      state.isLoading = false;
      state.newTags = [...action.payload.tags];
    },
    setChangeNewTag: (state, action) => {
      state.changeNewTag = action.payload;
    },
    setUserTags: (state, action) => {
      state.isLoadingUsers = false;
      if (Array.isArray(action.payload.results)) {
        state.uTags = action.payload.results;
        state.pageUtag = action.payload.totalUsers;
      } else {
        state.uTags = [];
        state.pageUtag = 0;
      }
    },
    setCleanUTag: (state) => {
      state.uTags = [];
      state.pageUtag = 0;
    },
    // Reducers para tag individual
    startLoadingCurrentTag: (state) => {
      state.isLoadingCurrentTag = true;
    },
    stopLoadingCurrentTag: (state) => {
      state.isLoadingCurrentTag = false;
    },
    setCurrentTag: (state, action) => {
      state.currentTag = action.payload;
      state.isLoadingCurrentTag = false;
    },
    clearCurrentTag: (state) => {
      state.currentTag = null;
      state.isLoadingCurrentTag = false;
    }
  }
});

export const {
  startLoadingTags,
  stopLoadingTags,
  setTagsPaginated,
  setTagsWithUsers, 
  setUsersPaginated,
  setError,
  setChangeTag,
  setTags,
  setUsersByTag,
  setProvisionalTagId,
  setSelectedTagId,
  setClearUsersTags,
  startLoadingUsers,
  stopLoadingUsers,
  setNewTags,
  setChangeNewTag,
  setUserTags,
  setCleanUTag,
  // Nuevos actions para tag individual
  startLoadingCurrentTag,
  stopLoadingCurrentTag,
  setCurrentTag,
  clearCurrentTag,
} = tagSlice.actions;
